import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import ProcessCircleBox from "@icoach/inbody/components/ProcessCircleBox";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { MASK_TYPE, Button, TextField, RadioField, Grid, Box } from "@curves/ui-base";
import { ReadTextField } from "@icoach/components/";
import { EmployeeSelect } from "@icoach/components/";
import { isEmpty, isEqual, isFunction, refIsRequiredError } from "@util/utils";
import useInBodyApi from "@apis/useInBodyApi";
import { MEASURE_REFERENCE_TYPE } from "@icoach/constants/";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { GENDER_TYPE } from "@constants/";

/** ================================
 * 📌 量身使用
 * 顯示目前InBody裝置有人在使用
 * onInBodyKickUserDialog  踢除用者介面
 * onInBodyImpedanceWarningDialog   阻抗有狀況介面
 * openProcessCircle 量身時百分比，由該元件控制
 *  ================================
 */
const InBodyMeasurementInputDialog = React.forwardRef((props, ref) => {
	const { open, onClose: onCloseProps, sourceData: sourceDataProps, refresh } = props;
	if (!open) return null;
	const { referenceType, peopleID, height, employeeID, gender = 1, nickName = "", isMenHidden, onInBodyKickUserDialog, onInBodyImpedanceWarningDialog, isInbodyOnly = false } = sourceDataProps || {};
	const { postTriggerMeasureApi } = useInBodyApi();
	const { employeeOption } = useGetEmployeeOptionState();
	const [openProcessCircle, setOpenProcessCircle] = useState(false); // 切換百分比,開啟時啟動singlr
	const employeesRef = useRef(null);
	const heightRef = useRef(null);
	const genderRef = useRef(null);
	const ageRef = useRef(null); // 只有訪客需要年齡，其餘都由後端判斷帶入

	const successRefreshApi = async () => {
		isFunction(refresh) && refresh();
	};

	const getParams = () => {
		let result = {
			referenceID: peopleID,
			employeeID: 0,
			gender: 1,
			referenceType,
		};

		if (ageRef.current && ageRef.current.getResult) {
			result.age = ageRef.current.getResult();
		}

		if (employeesRef.current && employeesRef.current.getResult) {
			result.employeeID = employeesRef.current.getResult();
		}

		if (heightRef.current && heightRef.current.getResult) {
			result.height = heightRef.current.getResult();
		}

		if (genderRef.current && genderRef.current.getResult) {
			result.gender = genderRef.current.getResult().find(target => target.checked)?.value ?? 1;
		}

		return result;
	};

	const handleClose = () => {
		setOpenProcessCircle(false);
		isFunction(onCloseProps) && onCloseProps();
	};

	const handleStartInBodyMeasure = () => {
		if (!refIsRequiredError(employeesRef, heightRef, ageRef)) {
			const params = getParams();
			doPrepareInbodyMeasureApi(params);
		}
	};

	const handleOpenImpedanceDialog = () => {
		onInBodyImpedanceWarningDialog();
	};

	const doPrepareInbodyMeasureApi = async params => {
		const resp = await postTriggerMeasureApi(params);
		if (resp) {
			if (resp?.isError) {
				isEqual(resp.statusCode, "ME003") && onInBodyKickUserDialog();
			} else {
				setOpenProcessCircle(true);
			}
		}
	};

	useEffect(
		() => {
			return () => handleClose();
		},
		// eslint-disable-next-line
		[]
	);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => getParams(),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<Dialog open={open} PaperProps={{ className: "wx-40rem" }} fullWidth>
			<GradientColorHeader onClose={handleClose}>新增量身</GradientColorHeader>
			{!openProcessCircle ? (
				<React.Fragment>
					<DialogContent>
						<Box className="pt-3 px-3">
							<Grid container spacing={2}>
								{!isEmpty(nickName) && (
									<Grid item xs={12}>
										<ReadTextField label="暱稱" direction="row" required>
											{nickName}
										</ReadTextField>
									</Grid>
								)}
								{!isEqual(referenceType, MEASURE_REFERENCE_TYPE.EMPLOYEE) && (
									<Grid item xs={12}>
										<EmployeeSelect ref={employeesRef} key={employeeID} options={employeeOption} label="擔當" defaultValue={employeeID} direction="row" required fullWidth />
									</Grid>
								)}
								<Grid item xs={12}>
									<TextField ref={heightRef} key={height} defaultValue={height} direction="row" label="身高" maskType={MASK_TYPE.POSITIVE_INT} fullWidth required />
								</Grid>
								{isEqual(referenceType, MEASURE_REFERENCE_TYPE.GUEST) && (
									<Grid item xs={12}>
										<TextField ref={ageRef} direction="row" defaultValue={18} label="年齡" maskType={MASK_TYPE.POSITIVE_INT} fullWidth required />
									</Grid>
								)}
								<Grid item xs={12}>
									<RadioField ref={genderRef} key={gender} defaultValue={gender || GENDER_TYPE.FEMALE} label="性別" labelDirection="row" required row>
										<RadioField.Radio value={GENDER_TYPE.FEMALE} label="女" />
										{!isMenHidden && <RadioField.Radio value={GENDER_TYPE.MALE} label="男" />}
									</RadioField>
								</Grid>
							</Grid>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleStartInBodyMeasure}>開始量身</Button>
					</DialogActions>
				</React.Fragment>
			) : (
				<DialogContent>
					<ProcessCircleBox
						className="pt-3 px-3"
						peopleID={peopleID}
						referenceType={referenceType}
						refresh={successRefreshApi}
						onClose={handleClose}
						onOpenImpedanceDialog={handleOpenImpedanceDialog}
						isInbodyOnly={isInbodyOnly}
					/>
				</DialogContent>
			)}
		</Dialog>
	);
});

export default InBodyMeasurementInputDialog;
